import { useEffect, useRef } from 'react';
import { track } from './track';

// The conversion funnel, client-side.
//
// These events answer "where do people stop on the way to a trial": who saw a
// paywall, who chose the free judging path instead, who dismissed, who clicked
// BEGIN FREE TRIAL. They ride Firebase Analytics through track() and are
// DIRECTIONAL, NOT DECISIONAL — the server-side ledger
// (functions/contracts/analytics.js) explicitly assigns paywall_viewed-style
// events to this side because an ad blocker can eat them, and it is fine that
// they undercount. The decisional halves of this funnel already exist server
// side: checkout_started, subscription_started, scan_unlocked.
//
// Every event carries a `surface` param naming WHERE it happened, because the
// same components render on several pages and an unattributed view answers
// nothing. Surfaces reuse the labels useStartTrial already tags its
// console.error with, so analytics and error reports name a surface the same
// way.
//
//   paywall_viewed { surface, gate }        a paywall became visible
//   ├── trial_cta_clicked { surface }       priced path (fires in useStartTrial,
//   │                                       every trial button routes through it)
//   ├── judge_path_clicked { surface }      the free "judge ten" alternative
//   └── paywall_dismissed { surface }       NOT NOW / Escape / backdrop
//
//   membership_screen_viewed { mode }       /membership reached
//   routine_screen_viewed { phase }         /routine reached (phase says locked?)
//   membership_manage_clicked               Stripe portal opened
//   membership_cancel_clicked               cancel flow opened
export const CONVERSION_EVENTS = {
  MEMBERSHIP_SCREEN_VIEWED: 'membership_screen_viewed',
  ROUTINE_SCREEN_VIEWED: 'routine_screen_viewed',
  PAYWALL_VIEWED: 'paywall_viewed',
  PAYWALL_DISMISSED: 'paywall_dismissed',
  TRIAL_CTA_CLICKED: 'trial_cta_clicked',
  JUDGE_PATH_CLICKED: 'judge_path_clicked',
  MEMBERSHIP_MANAGE_CLICKED: 'membership_manage_clicked',
  MEMBERSHIP_CANCEL_CLICKED: 'membership_cancel_clicked',
};

// Fire a view event ONCE per mount, the first render where `when` is true.
//
// A ref guard rather than an effect dependency list on purpose: params are
// rebuilt object literals every render, so depending on them would re-fire the
// event on any parent re-render and turn one view into dozens. The effect runs
// each render and the ref decides; the params sent are the ones from the
// render that first satisfied `when`.
export function useTrackViewOnce(eventName, params = {}, when = true) {
  const sentRef = useRef(false);
  useEffect(() => {
    if (!when || sentRef.current) return;
    sentRef.current = true;
    track(eventName, params);
  });
}
